import { useEffect, useState } from 'react';
import { api, type MeetRoomSummary } from '../api';
import { Button, Input, Icon, Modal, Spinner } from './ui';
import { toast } from './Toast';

// =============================================================================
// NewMeetingModal — create an instant meeting room and share the link.
// =============================================================================
// Built on the shared `Modal`, so it inherits the dialog semantics, focus trap
// and focus restore rather than hand-rolling an overlay.
//
// The room is created on open, not on a second click: the whole point of the
// feature is "I need a call link right now", and a create step between the
// click and the link is friction with no purpose. If creation fails (usually
// the per-host room cap) the modal says so and offers the way out.
// =============================================================================

function absoluteUrl(url: string): string {
  // The backend returns a relative /join/:code when APP_PUBLIC_URL isn't
  // configured. Resolve against the current origin so the copied link works.
  return url.startsWith('http') ? url : `${window.location.origin}${url}`;
}

export function NewMeetingModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [room, setRoom] = useState<MeetRoomSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      // Reset so the next open starts clean — the component stays mounted.
      setRoom(null);
      setError(null);
      setCopied(false);
      return;
    }
    let cancelled = false;
    api
      .createMeetRoom()
      .then((r) => {
        if (!cancelled) setRoom(r);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Could not create a meeting');
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const link = room ? absoluteUrl(room.url) : '';

  async function copy() {
    if (!link) return;
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(new Error('Copy failed'), 'Could not copy the link');
    }
  }

  return (
    <Modal open={open} onClose={onClose} title="Start a meeting" width="w-[460px]">
      <div className="p-5 space-y-4">
        {error ? (
          <div className="space-y-3">
            <p className="text-sm text-text">{error}</p>
            <Button onClick={onClose} className="w-full">
              Close
            </Button>
          </div>
        ) : !room ? (
          <div className="flex flex-col items-center gap-3 py-6 text-text-muted">
            <Spinner size={20} />
            <span className="text-sm">Setting up your room…</span>
          </div>
        ) : (
          <>
            <p className="text-sm text-text-muted">
              Anyone with this link can join — no account needed. The meeting expires
              automatically after 12 hours, or when you end it.
            </p>

            <div>
              <label
                htmlFor="meet-link"
                className="block text-[10px] uppercase tracking-widest text-text-subtle font-bold mb-1"
              >
                Invite link
              </label>
              <div className="flex gap-2">
                <Input
                  id="meet-link"
                  value={link}
                  readOnly
                  onFocus={(e) => e.currentTarget.select()}
                  className="flex-1 font-mono text-xs"
                />
                <Button onClick={() => void copy()} className="shrink-0">
                  <Icon name={copied ? 'check' : 'copy'} size={14} />
                  <span className="ml-1">{copied ? 'Copied' : 'Copy'}</span>
                </Button>
              </div>
            </div>

            <div className="flex gap-2 pt-1">
              <a
                href={room.url}
                className="aim-button flex-1 text-center px-3 py-2 text-sm font-semibold"
              >
                Join now
              </a>
              <Button onClick={onClose} className="shrink-0">
                Later
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
